import { useEffect, useMemo, useRef, useState } from "react";

import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { useEventListener, useOnClickOutside } from "usehooks-ts";

import { useUser } from "@components/user-context";
import {
  FloatingPortal,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { DatePicker } from "@shared/date-picker";
import { DateRangeCalendar } from "@shared/date-range-calendar";
import { FormattedDateRange } from "@shared/date-range-picker";

import { type NewPromotionCalendar } from "./promotion-calendar-modal";

interface PromotionCalendarDatePickerProps {
  currentPromotionCalendar: NewPromotionCalendar;
  watch: UseFormWatch<NewPromotionCalendar>;
  setValue: UseFormSetValue<NewPromotionCalendar>;
  errors: FieldErrors<NewPromotionCalendar>;
  control: Control<NewPromotionCalendar>;
}

export const PromotionCalendarDatePicker = ({
  currentPromotionCalendar,
  watch,
  setValue,
  errors,
  control,
}: PromotionCalendarDatePickerProps) => {
  const { user } = useUser();
  const annotatedDate = watch("annotatedDate");
  const endDate = watch("endDate");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const dateRange = useMemo(
    () => ({
      startDate: annotatedDate || null,
      endDate: endDate || annotatedDate || null,
    }),
    [annotatedDate, endDate],
  );

  const ref = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { refs, floatingStyles } = useFloating({
    open: isCalendarOpen,
    onOpenChange: setIsCalendarOpen,
    transform: false,
    placement: "bottom",
    middleware: [offset(8), flip(), shift()],
  });

  if (anchorRef.current && ref.current) {
    computePosition(anchorRef.current, ref.current).then(({ x, y }) => {
      if (ref.current?.style) {
        Object.assign(ref.current.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      }
    });
  }
  useOnClickOutside(ref, () => setIsCalendarOpen(false));
  useEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsCalendarOpen(false);
      event.stopPropagation();
    }
  });

  if ("endDate" in currentPromotionCalendar) {
    const onChange = (date: { startDate: string; endDate: string }) => {
      setValue("annotatedDate", date.startDate);
      setValue("endDate", date.endDate);
      setIsCalendarOpen(false);
    };

    return (
      <div className="nb-input-container text-sm relative">
        <label className="label">DATE RANGE</label>
        <div
          className="nb-input"
          ref={anchorRef}
          onClick={() =>
            setIsCalendarOpen((prevIsCalendarOpen) => !prevIsCalendarOpen)
          }
        >
          {annotatedDate && endDate ? (
            <span>
              <FormattedDateRange
                dateRange={{ startDate: annotatedDate, endDate }}
              />
            </span>
          ) : (
            <span className="text-disabled">Select...</span>
          )}
        </div>
        {isCalendarOpen && (
          <FloatingPortal root={document.body}>
            <div
              ref={(r) => {
                ref.current = r;
                refs.setFloating(r);
              }}
              style={floatingStyles}
              className="z-[2002]"
            >
              <DateRangeCalendar
                className="bg-v2-primary rounded-lg elevation-1"
                value={dateRange}
                onApply={onChange}
                onCancel={() => setIsCalendarOpen(false)}
              />
            </div>
          </FloatingPortal>
        )}
      </div>
    );
  }

  return (
    <>
      <Controller
        control={control}
        name="endDate"
        rules={{ required: "Date is required" }}
        render={() => <></>}
      />
      <Controller
        control={control}
        name="annotatedDate"
        rules={{ required: "Date is required" }}
        render={({ field }) => {
          const onChange = (date: string | null) => field.onChange(date);
          return (
            <DatePicker
              {...field}
              onChange={onChange}
              name="annotatedDate"
              timezone={user.timezone}
              errors={errors}
            />
          );
        }}
      />
    </>
  );
};
